import { useApolloClient } from "@apollo/client";
import { Modal } from "@msys/ui";
import { Stack } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { Form, Formik } from "formik";
import { uniqueId } from "lodash-es";
import { useSnackbar } from "notistack";
import React from "react";
import * as Yup from "yup";
import { TextField } from "../../commons/form-fields/TextField.js";
import { useUpdateProductListMutation } from "./EditProductListModal.generated.js";

interface FormValues {
  title: string;
}

export const EditProductListModal = ({
  productListId,
  title,
  handleComplete,
  handleClose,
}: {
  productListId: string;
  title: string;
  handleClose(): void;
  handleComplete?(productListId: string): void;
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslate(["Global"]);

  const client = useApolloClient();
  const [update] = useUpdateProductListMutation({ client });

  const handleSubmit = async (values: FormValues) => {
    try {
      const result = await update({
        variables: {
          input: {
            productListId,
            values: {
              title: values.title,
            },
          },
        },
      });
      if (result.data?.updateProductList.productList.id) {
        enqueueSnackbar(
          t("Saved", {
            ns: "Global",
          }),
          { variant: "success" }
        );
        handleComplete?.(result.data?.updateProductList.productList.id);
        handleClose();
      }
    } catch (e) {
      if (e instanceof Error) enqueueSnackbar(e.message, { variant: "error" });
    }
  };

  const initialValues: FormValues = {
    title,
  };

  const validationSchema = React.useMemo(
    () =>
      Yup.object().shape({
        title: Yup.string()
          .label(t("Title", { ns: "Global" }))
          .required(),
      }),
    [t]
  );

  const formId = React.useMemo(() => uniqueId(), []);

  return (
    <Formik<FormValues>
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      initialValues={initialValues}
    >
      {({ isSubmitting, dirty, isValid }) => (
        <Modal
          title={t("Edit", {
            ns: "Global",
          })}
          handleClose={handleClose}
          actionButtons={[
            {
              label: t("Close", { ns: "Global" }),
              handleClick: handleClose,
              buttonProps: { variant: "text" },
            },
            {
              label: t("Save", { ns: "Global" }),
              buttonProps: {
                loading: isSubmitting,
                form: formId,
                type: "submit",
                disabled: !dirty || !isValid,
              },
            },
          ]}
        >
          <Form id={formId}>
            <Stack spacing={1} direction="column">
              <TextField
                name="title"
                required
                label={t("Title", {
                  ns: "Global",
                })}
              />
            </Stack>
          </Form>
        </Modal>
      )}
    </Formik>
  );
};
